import kebabCase from 'lodash/kebabCase';
import { isIntersecting, isSubsetOf } from '@/utilities/helpers.js';

const LABELS = {};

/**
 * Add an enum variant to the label list
 *
 * @param {string} statusEnum The enum to add the variant to
 * @param {string} value The internal identifier for the variant
 * @param {string} label The pretty label for the variant
 * @param {string[]} flags Any other flags (for filtering)
 */
function addEnum(statusEnum, value, label, flags = []) {
  if (!LABELS[statusEnum]) LABELS[statusEnum] = {};
  LABELS[statusEnum][value] = { id: value, label, flags };
}

// TODO Auto generate from rust source?
addEnum('ActorStatus', 'IN_WIZARD', 'Incomplete');
addEnum('ActorStatus', 'IN_REVIEW', 'In Review');
addEnum('ActorStatus', 'ACTIVE', 'Active');
addEnum('RoleTypeFilter', 'MALE_PRESENTING_ROLE', 'Male Presenting');
addEnum('RoleTypeFilter', 'FEMALE_PRESENTING_ROLE', 'Female Presenting');
addEnum('RoleTypeFilter', 'NON_BINARY_ROLE', 'Non-Binary role');
addEnum('HeightComparatorFilter', 'GREATER_THAN_OR_EQUAL', 'Greater than');
addEnum('HeightComparatorFilter', 'LESS_THAN_OR_EQUAL', 'Less than');
addEnum('ShowRoleProspectStatus', 'UNREVIEWED', 'Unreviewed', ['creative']);
addEnum('ShowRoleProspectStatus', 'REJECTED', 'NTS', ['creative']);
addEnum('ShowRoleProspectStatus', 'POSSIBLE', 'Possible', ['creative']);
addEnum('ShowRoleProspectStatus', 'CALLBACK', 'Callback', ['creative']);
addEnum('ShowRoleProspectStatus', 'UNAVAILABLE', 'Unavailable');
addEnum('ShowRoleProspectStatus', 'DECLINED', 'Declined');
addEnum('ShowRoleProspectStatus', 'NOT_OFFERED', 'Not Offered', ['creative', 'not_selectable']);
addEnum('ShowRoleProspectStatus', 'PITCHED', 'Pitched', ['not_selectable']);
addEnum('ShowRoleProspectStatus', 'READY', 'Ready', ['not_selectable']);
addEnum('ShowRoleProspectStatus', 'OFFERED', 'Offered', ['not_selectable']);
addEnum('ShowRoleProspectStatus', 'ACCEPTED', 'Accepted', ['not_selectable']);
addEnum('ShowRoleStatus', 'OPEN', 'Open');
addEnum('ShowRoleStatus', 'ASSIGNED', 'Assigned', ['not_selectable']);
addEnum('ShowRoleStatus', 'NOT_NEEDED', 'Not needed');
// TODO mark flags for these 4 as ,['assigned']
addEnum('ShowRoleStatus', 'PITCHED', 'Pitched');
addEnum('ShowRoleStatus', 'READY', 'Ready');
addEnum('ShowRoleStatus', 'OFFERED', 'Offered');
addEnum('ShowRoleStatus', 'ACCEPTED', 'Accepted');
addEnum('ShowStatus', 'PLANNING', 'Planning');
addEnum('ShowStatus', 'ACCEPTING_APPLICATIONS', 'Accepting applications');
addEnum('ShowStatus', 'FULLY_CAST', 'Fully cast');
addEnum('ShowStatus', 'RUNNING', 'Running');
addEnum('ShowStatus', 'COMPLETED', 'Completed');
addEnum('ShowStatus', 'CANCELED', 'Canceled');
addEnum('SeasonStatus', 'OPEN', 'Open');
addEnum('SeasonStatus', 'ADMIN_ONLY', 'Owner Only');
addEnum('ActorAffiliation', 'AEA', 'AEA');
addEnum('ActorAffiliation', 'NON_UNION', 'Non-Union');
addEnum('ActorSkillRank', 'ACTING', 'Actor');
addEnum('ActorSkillRank', 'DANCING', 'Dancer');
addEnum('ActorSkillRank', 'SINGING', 'Singer');
addEnum('PrefabFilterModel', 'ACTOR', 'Actor');
addEnum('PrefabFilterModel', 'SHOW', 'Show');
addEnum('UserRole', 'ADMIN', 'Owner');
addEnum('UserRole', 'MANAGER', 'Admin');
addEnum('UserRole', 'THEATER_MANAGER', 'Manager');
addEnum('UserRole', 'CREATIVE', 'Creative');
addEnum('NoteVisibility', 'PRIVATE', 'Private');
addEnum('NoteVisibility', 'CREATIVE_VISIBLE', 'Visible to Creatives', ['creative']);

/**
 * Get the list of ordered enum values
 *
 * @param {string} statusEnum Which enum to fetch values for
 * @param {Object} filters A filter configuration to limit which variants are returned
 * @param {string[]} filters.only A list of flags that MUST be set on the variant
 * @param {string[]} filters.except A list of flags that MUST be set on the variant
 * @return {object[]}
 */
export function statusEnumValues(statusEnum, filters = null) {
  const values = Object.values(LABELS[statusEnum]);
  if (filters) {
    return statusFilterByFlags(values, filters);
  } else {
    return values;
  }
}

/**
 * Get the list of ordered enum values
 *
 * @param {object} status The status to check against the filter
 * @param {Object} filters A filter configuration to limit which variants are returned
 * @param {string[]} filters.only A list of flags that MUST be set on the variant
 * @param {string[]} filters.except A list of flags that MUST be set on the variant
 * @return {object}
 */
export function statusCheckFlags(status, filters) {
  let good = true;
  if (filters.only) {
    good &= isSubsetOf(status.flags, filters.only);
  }
  if (filters.except) {
    good &= !isIntersecting(status.flags, filters.except);
  }
  return good;
}

/**
 * Get the list of ordered enum values
 *
 * @param {object[]} statuses The statuses to filter
 * @param {Object} filters A filter configuration to limit which variants are returned
 * @param {string[]} filters.only A list of flags that MUST be set on the variant
 * @param {string[]} filters.except A list of flags that MUST be set on the variant
 * @return {object[]}
 */
export function statusFilterByFlags(statuses, filters) {
  return statuses.filter(a => statusCheckFlags(a, filters));
}

export function statusEnumLabel(statusEnum, value) {
  return LABELS[statusEnum]?.[value]?.label || value;
}

export function statusEnumClasses(statusEnum, value) {
  return [
    kebabCase(statusEnum),
    value?.toLowerCase(),
  ];
}
